/**
 * RecentDocumentsList — "Últimos documentos" da home.
 *
 * Antes esta seção era "Últimos orçamentos" e lia só `quotes`. Agora mistura os
 * seis tipos que o app gera, então cada linha precisa dizer O QUE ela é — numa
 * lista de um tipo só, o tipo é o título da seção; numa lista misturada, sem
 * marcação o usuário não sabe se "#0012 · R$ 340,00" é um orçamento a aprovar
 * ou um recibo já emitido.
 *
 * As CORES DO TIPO são as mesmas dos atalhos logo acima (laranja = ordem de
 * serviço, violeta = contrato, verde-água = recibo, índigo = nota, esmeralda =
 * venda). Não é enfeite: é o que liga a linha ao ícone pelo qual a pessoa
 * chegaria no módulo, sem precisar ler o rótulo.
 */
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Clock,
  FileSignature,
  FileText,
  Inbox,
  Receipt,
  ReceiptText,
  ShoppingCart,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { CONTENT_CARD } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { formatBRL } from '@/lib/financeLabels';
import {
  timelineKindLabel,
  timelineStatusLabel,
  type ClientTimelineEntry,
  type ClientTimelineKind,
} from '@/lib/clientSummary';
import { NFSE_STATUS_META } from '@/types/fiscal';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

type Tone = 'neutral' | 'progress' | 'success' | 'error';

/** Ícone e cor por tipo — espelham os atalhos da grade acima. */
const KIND_VISUALS: Record<ClientTimelineKind, { icon: LucideIcon; badge: string }> = {
  quote: { icon: FileText, badge: 'bg-primary/10 text-primary' },
  sale: { icon: ShoppingCart, badge: 'bg-emerald-500/10 text-emerald-600' },
  service_order: { icon: ClipboardList, badge: 'bg-orange-500/10 text-orange-600' },
  contract: { icon: FileSignature, badge: 'bg-violet-500/10 text-violet-600' },
  receipt: { icon: ReceiptText, badge: 'bg-teal-500/10 text-teal-600' },
  nfse: { icon: Receipt, badge: 'bg-indigo-500/10 text-indigo-600' },
  // Compromisso não entra nesta lista (ver `recentDocuments.ts`); fica mapeado
  // só para o Record ser total e o `tsc` cobrar um tipo novo.
  appointment: { icon: Clock, badge: 'bg-sky-500/10 text-sky-600' },
};

/**
 * Tom por status, para TODOS os tipos.
 *
 * Deliberadamente único: numa lista misturada, "Cancelado" precisa ter a mesma
 * cor sendo orçamento ou nota — duas cores para a mesma palavra na mesma tela
 * lê como defeito. Por isso este mapa vem ANTES do `NFSE_STATUS_META`, que
 * classifica `cancelled` como neutro dentro do módulo fiscal.
 */
const STATUS_TONES: Record<string, Tone> = {
  pending: 'neutral',
  draft: 'neutral',
  scheduled: 'neutral',
  sent: 'progress',
  partially_signed: 'progress',
  in_progress: 'progress',
  approved: 'success',
  completed: 'success',
  signed: 'success',
  issued: 'success',
  done: 'success',
  cancelled: 'error',
  rejected: 'error',
};

const TONE_CLASSES: Record<Tone, string> = {
  neutral: 'bg-muted text-muted-foreground',
  progress: 'bg-blue-500/10 text-blue-600',
  success: 'bg-emerald-500/10 text-emerald-700',
  error: 'bg-red-500/10 text-red-600',
};

function statusTone(kind: ClientTimelineKind, status: string): Tone {
  const shared = STATUS_TONES[status];
  if (shared) return shared;
  // Os status exclusivos do fiscal (na fila, enviando, reconciliar…) já têm tom
  // declarado no próprio módulo — não vale reescrevê-los aqui.
  if (kind === 'nfse') return NFSE_STATUS_META[status as keyof typeof NFSE_STATUS_META]?.tone ?? 'neutral';
  return 'neutral';
}

/**
 * Os tipos que sabem enviar mensagem ao cliente.
 *
 * O diálogo de envio monta o texto com `buildQuoteMessageContext`, que fala de
 * ORÇAMENTO (número, validade, link público). Mostrar o botão numa ordem de
 * serviço abriria uma mensagem sobre outro documento — e mostrá-lo desligado
 * seria um botão que não faz nada, que é pior do que não ter botão.
 */
const WHATSAPP_KINDS: ReadonlySet<ClientTimelineKind> = new Set<ClientTimelineKind>(['quote', 'sale']);

/** O telefone do cliente, quando o tipo suporta envio e a linha o carrega. */
function clientPhoneOf(entry: ClientTimelineEntry): string {
  if (!WHATSAPP_KINDS.has(entry.kind)) return '';
  return String((entry.ref as any)?.client_phone ?? '').replace(/\D/g, '');
}

interface RecentDocumentsListProps {
  entries: ClientTimelineEntry[];
  loading: boolean;
  /**
   * Envio por WhatsApp. Só orçamento e venda o recebem: o diálogo de mensagem
   * monta o contexto com `buildQuoteMessageContext`, que é do orçamento —
   * oferecer o botão numa OS abriria um texto que fala de outro documento.
   */
  onSendWhatsApp?: (entry: ClientTimelineEntry) => void;
  /** Ação do estado vazio (ausente para visitante). */
  emptyAction?: React.ReactNode;
}

export function RecentDocumentsList({
  entries,
  loading,
  onSendWhatsApp,
  emptyAction,
}: RecentDocumentsListProps) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className={cn(CONTENT_CARD, 'h-20 animate-pulse')} />
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className={cn(CONTENT_CARD, 'flex flex-col items-center justify-center border-dashed py-10 text-center')}>
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
          <Inbox className="h-7 w-7 text-muted-foreground/50" />
        </div>
        <p className="font-medium text-muted-foreground">Seus documentos aparecerão aqui</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Orçamentos, vendas, ordens de serviço, contratos, recibos e notas.
        </p>
        {emptyAction}
      </div>
    );
  }

  return (
    /* Duas colunas no desktop, como as listas do app (#185: `grid-cols-1`
       explícito no celular, senão a coluna implícita cresce até o min-content
       do título mais longo e vaza a página). */
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
      {entries.map((entry) => {
        const visual = KIND_VISUALS[entry.kind];
        const Icon = visual.icon;
        const phone = onSendWhatsApp ? clientPhoneOf(entry) : '';
        const sequenceId = (entry.ref as any)?.sequence_id;
        const clientName = (entry.ref as any)?.client_name ?? (entry.ref as any)?.payer_name;
        const pending = (entry.ref as any)?._offline_pending === true;

        return (
          <div
            key={`${entry.kind}:${entry.id}`}
            className={cn(
              CONTENT_CARD,
              'flex cursor-pointer items-center gap-3 p-4 transition-shadow hover:shadow-md',
            )}
            onClick={() => navigate(entry.route)}
          >
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                {/* O TIPO vem primeiro: é o que diferencia esta lista da antiga. */}
                <span
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold',
                    visual.badge,
                  )}
                >
                  <Icon className="h-3 w-3" />
                  {timelineKindLabel(entry.kind)}
                </span>
                {pending && (
                  <span className="inline-flex items-center text-amber-600" title="Aguardando sincronização">
                    <Clock className="h-3.5 w-3.5" />
                  </span>
                )}
                {sequenceId ? (
                  <Badge
                    variant="outline"
                    className="h-5 border-primary/30 bg-primary/5 px-1.5 py-0 font-mono text-xs text-primary"
                  >
                    #{String(sequenceId).padStart(4, '0')}
                  </Badge>
                ) : null}
              </div>

              {/* `min-w-0` + sem clamp: nome de documento é texto livre e é ele
                  que distingue duas linhas do mesmo tipo (#175). */}
              <p className="min-w-0 break-words font-medium leading-tight">{entry.title}</p>

              {clientName ? (
                <p className="truncate text-sm text-muted-foreground">{clientName}</p>
              ) : null}

              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs text-muted-foreground">
                  {format(entry.date, "dd 'de' MMM", { locale: ptBR })}
                </p>
                <Badge className={cn('hover:bg-transparent', TONE_CLASSES[statusTone(entry.kind, entry.status)])}>
                  {timelineStatusLabel(entry.kind, entry.status)}
                </Badge>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {/* Compromisso é o único tipo sem valor — e ele nem entra aqui.
                  O guard fica porque `amount` é `number | null` no contrato. */}
              {entry.amount !== null && <p className="font-semibold">{formatBRL(entry.amount)}</p>}
              {phone && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSendWhatsApp?.(entry);
                  }}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-green-600 transition-colors hover:bg-green-100"
                  aria-label="Enviar mensagem pelo WhatsApp"
                >
                  <WhatsAppIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
